/**
 * The `roomSetup` route — where the building screen sends a surveyor to start a
 * machine room, and where the crumb and the hall indicator's "Room setup" button
 * bring them back.
 *
 * ── Two states, one section ──────────────────────────────────────────────────
 * `MachineRoom.type` has no "unset" state, so a room is CREATED BY THE M1 TAP:
 * the building screen mints an id and routes here, and until a type is chosen
 * there is nothing in the document — no room, and therefore no `room_setup`
 * section for the walk to render. That is what keeps "answer this first"
 * literally true instead of showing a pre-answered Traction the surveyor never
 * chose. So:
 *
 *   - **no room yet** → the type picker below, which creates the room;
 *   - **the room exists** → the room's `room_setup` SECTION SCREEN, byte for
 *     byte the screen the hall indicator's "Room Setup" row and Previous from
 *     Electrical open — header, wizard sentence, the setup builder as its body
 *     (`RoomSetupBody`), notes and photos, the D-021 reason at the moment of
 *     leaving, and Previous / Next.
 *
 * ── Why this used to be a whole screen of its own — Chip, 2026-08-18 ────────
 * It carried the builder itself, with a bottom bar of "Building | Sections",
 * while the section route rendered the same section GENERICALLY: M1–M7 as
 * question rows with nothing to tap, and *"Car 4 isn't in a hoistway yet."* in
 * red under an empty hoistway control. Two screens for one section, and the one
 * that could fix the problem was not the one the sentence was on:
 *
 *   *"There was no option to edit or correct the error, only the message
 *    appeared. … we could not return to the room-setup screen."*
 *
 * There is now one screen for the section, and this route is a door to it.
 * See `STRUCTURE_SECTIONS` in core/sections.ts for the declaration a screen
 * consults, and `RoomSetupBody` for the builder.
 */

import type { ReactNode } from 'react';

import { Card, Eyebrow, Segmented } from '../components';
import { sectionInstanceId, type RoomType } from '../../core/types';
import { useSurvey } from '../state/SurveyProvider';
import { pendingRoomSlots } from '../state/buildingSetup';
import { defaultRoomName, newMachineRoom, roomTypeOptions } from '../state/roomSetup';
import { SectionScreen } from './SectionScreen';

export interface RoomSetupScreenProps {
  surveyId: string;
  roomId: string;
}

export function RoomSetupScreen({ surveyId, roomId }: RoomSetupScreenProps): ReactNode {
  const { room } = useSurvey();
  const machineRoom = room(roomId);

  // The room exists: it has a section, and the section has one screen. Nothing
  // is decided here — not the crumb, not the nav — so this route can never show
  // a different Room Setup than the walk does.
  if (machineRoom) {
    return (
      <SectionScreen
        surveyId={surveyId}
        sectionId={sectionInstanceId('room_setup', { roomId })}
      />
    );
  }

  return <NewRoomScreen roomId={roomId} />;
}

/**
 * The room before it exists: one question, and it creates the room.
 *
 * A component of its own so the branch that renders it stays small and its
 * one write — creating the room — is easy to see.
 */
function NewRoomScreen({ roomId }: Pick<RoomSetupScreenProps, 'roomId'>): ReactNode {
  const { setStructure, readOnly } = useSurvey();
  const typeOptions = roomTypeOptions();

  const createRoom = (type: RoomType): void => {
    void setStructure((document) => {
      // Idempotent: a double tap on a 44px button must not create two rooms.
      if (document.rooms.some((candidate) => candidate.id === roomId)) return;
      // The room takes the NAME OF THE ROW THE SURVEYOR TAPPED (final review,
      // F2), not `defaultRoomName` — which only ever counts `rooms.length` and
      // so renamed the second hollow row's room after the first one's default
      // name whenever a lower-numbered slot was still pending. `roomId` is the
      // slot this screen was opened for; falling back to `defaultRoomName` is
      // only for a door this route has no other way to be reached by (a direct
      // URL to an id `pendingRoomSlots` does not currently list).
      const name = pendingRoomSlots(document).find((slot) => slot.roomId === roomId)?.name ?? defaultRoomName(document);
      document.rooms.push(newMachineRoom({ roomId, name, type }));
    });
  };

  // D-030: the label needs no "answer first" — it IS first — and the MRL
  // ruleline was instruction. The segmented control is the whole screen.
  return (
    <>
      <Eyebrow>Room type</Eyebrow>
      <Card>
        <Segmented
          options={typeOptions}
          value={undefined}
          ariaLabel="Room type"
          onChange={createRoom}
          disabled={readOnly}
        />
      </Card>
    </>
  );
}
